import { Request } from 'api';
import { AxiosResponse } from 'axios';
import {
    LpsDevice,
    LpsShipment,
    LpsInstallation,
    LpsCustodyEvent,
    LpsDeviceStatusCounts,
    MqttCredentials,
    CreateShipmentParams,
    CreateInstallationParams,
    UpdateDeviceParams,
    UpdateInstallationDevicesParams,
} from './lps.types';

interface PaginatedResponse<T> {
    data: Array<T>;
    links: any;
    meta: {
        total: number;
        current_page: number;
        per_page: number;
        /** Present on Laravel paginated JSON responses */
        last_page?: number;
    };
}

// ── Devices ──────────────────────────────────────────────────────────────

interface GetDevicesParams {
    status?: string;
    device_type?: string;
    installation_uuid?: string;
    organization_uuid?: string;
    shipment_uuid?: string;
    serial_number?: string;
    page?: number;
    per_page?: number;
}

const getDevices = (
    params?: GetDevicesParams,
): Promise<AxiosResponse<PaginatedResponse<LpsDevice>>> => {
    const filterParams: Record<string, any> = {};
    if (params?.status) filterParams.status = params.status;
    if (params?.device_type) filterParams.device_type = params.device_type;
    if (params?.installation_uuid) filterParams.installation_uuid = params.installation_uuid;
    if (params?.organization_uuid) filterParams.organization_uuid = params.organization_uuid;
    if (params?.shipment_uuid) filterParams.shipment_uuid = params.shipment_uuid;
    if (params?.serial_number) filterParams.serial_number = params.serial_number;
    if (params?.page) filterParams.page = params.page;
    if (params?.per_page) filterParams.per_page = params.per_page;
    return Request.get('admin/lps/devices', { params: filterParams });
};

const getDeviceStatusCounts = (): Promise<AxiosResponse<{ data: LpsDeviceStatusCounts }>> => {
    return Request.get('admin/lps/devices/status-counts');
};

const getDevice = (uuid: string): Promise<AxiosResponse<{ data: LpsDevice }>> => {
    return Request.get(`admin/lps/devices/${uuid}`);
};

const updateDevice = (
    uuid: string,
    data: UpdateDeviceParams,
): Promise<AxiosResponse<{ data: LpsDevice }>> => {
    return Request.put(`admin/lps/devices/${uuid}`, data);
};

const addDeviceNote = (
    uuid: string,
    notes: string,
): Promise<AxiosResponse<{ data: LpsDevice }>> => {
    return Request.post(`admin/lps/devices/${uuid}/note`, { notes });
};

const getDeviceCustodyHistory = (
    uuid: string,
): Promise<AxiosResponse<LpsCustodyEvent[] | { data: LpsCustodyEvent[] }>> => {
    return Request.get(`admin/lps/devices/${uuid}/custody`);
};

// ── Shipments ────────────────────────────────────────────────────────────

interface GetShipmentsParams {
    supplier_name?: string;
    po_number?: string;
    page?: number;
    per_page?: number;
}

const getShipments = (
    params?: GetShipmentsParams,
): Promise<AxiosResponse<PaginatedResponse<LpsShipment>>> => {
    const filterParams: Record<string, any> = {};
    if (params?.supplier_name) filterParams.supplier_name = params.supplier_name;
    if (params?.po_number) filterParams.po_number = params.po_number;
    if (params?.page) filterParams.page = params.page;
    if (params?.per_page) filterParams.per_page = params.per_page;
    return Request.get('admin/lps/shipments', { params: filterParams });
};

const getShipment = (uuid: string): Promise<AxiosResponse<{ data: LpsShipment }>> => {
    return Request.get(`admin/lps/shipments/${uuid}`);
};

const createShipment = (
    data: CreateShipmentParams,
): Promise<AxiosResponse<{ data: LpsShipment }>> => {
    return Request.post('admin/lps/shipments', data);
};

const updateShipment = (
    uuid: string,
    data: Partial<CreateShipmentParams>,
): Promise<AxiosResponse<{ data: LpsShipment }>> => {
    return Request.put(`admin/lps/shipments/${uuid}`, data);
};

// ── Installations ────────────────────────────────────────────────────────

interface GetInstallationsParams {
    status?: string;
    organization_uuid?: string;
    page?: number;
    per_page?: number;
}

const getInstallations = (
    params?: GetInstallationsParams,
): Promise<AxiosResponse<PaginatedResponse<LpsInstallation>>> => {
    const filterParams: Record<string, any> = {};
    if (params?.status) filterParams.status = params.status;
    if (params?.organization_uuid) filterParams.organization_uuid = params.organization_uuid;
    if (params?.page) filterParams.page = params.page;
    if (params?.per_page) filterParams.per_page = params.per_page;
    return Request.get('admin/lps/installations', { params: filterParams });
};

const getInstallation = (uuid: string): Promise<AxiosResponse<{ data: LpsInstallation }>> => {
    return Request.get(`admin/lps/installations/${uuid}`);
};

const createInstallation = (
    data: CreateInstallationParams,
): Promise<AxiosResponse<{ data: LpsInstallation }>> => {
    return Request.post('admin/lps/installations', data);
};

const updateInstallation = (
    uuid: string,
    data: Partial<CreateInstallationParams>,
): Promise<AxiosResponse<{ data: LpsInstallation }>> => {
    return Request.put(`admin/lps/installations/${uuid}`, data);
};

const associateInstallationDevices = (
    installationUuid: string,
    data: UpdateInstallationDevicesParams,
): Promise<AxiosResponse<{ data: LpsInstallation }>> => {
    return Request.post(`admin/lps/installations/${installationUuid}/devices/associate`, data);
};

const dissociateInstallationDevices = (
    installationUuid: string,
    data: UpdateInstallationDevicesParams,
): Promise<AxiosResponse<{ data: LpsInstallation }>> => {
    return Request.post(`admin/lps/installations/${installationUuid}/devices/dissociate`, data);
};

const getInstallationMqttCredentials = (
    installationUuid: string,
): Promise<AxiosResponse<MqttCredentials>> => {
    return Request.get(`admin/lps/installations/${installationUuid}/mqtt-credentials`);
};

export {
    getDevices,
    getDeviceStatusCounts,
    getDevice,
    updateDevice,
    addDeviceNote,
    getDeviceCustodyHistory,
    getShipments,
    getShipment,
    createShipment,
    updateShipment,
    getInstallations,
    getInstallation,
    createInstallation,
    updateInstallation,
    associateInstallationDevices,
    dissociateInstallationDevices,
    getInstallationMqttCredentials,
};

export type { GetDevicesParams, GetShipmentsParams, GetInstallationsParams };
